import React, { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { Building2, Save, User, Phone, Mail, Briefcase, Shield, Lock, Eye, EyeOff, RefreshCw } from "lucide-react";
import toast from "react-hot-toast";
import { authAPI } from "../services/api";

const API = process.env.REACT_APP_API_URL || "http://localhost:8000/api/v1";

const INDUSTRIES = [
  "Oil & Gas", "Construction", "Healthcare", "Information Technology",
  "Agriculture", "Energy", "Security Services", "Legal Services",
  "Catering & Hospitality", "Telecommunications", "Engineering",
  "Environment", "Water & Sanitation", "Humanitarian", "Manufacturing", "Other",
];
const CATEGORIES = [
  "Works", "Goods", "Services", "Consultancy", "ICT", "Healthcare",
  "Education", "Oil & Gas", "Agriculture", "Security", "Environment",
];
const STATES = [
  "Federal", "Rivers", "Lagos", "Abuja (FCT)", "Delta", "Kano",
  "Anambra", "Ogun", "Oyo", "Kaduna", "Borno", "Enugu",
  "Imo", "Bayelsa", "Cross River", "Akwa Ibom", "Edo",
];

const inputStyle = {
  width: "100%", padding: "10px 14px",
  background: "var(--bg-secondary)", border: "1px solid var(--border)",
  borderRadius: "8px", color: "var(--text-primary)", fontSize: "13px", outline: "none",
};

function Toggle({ enabled, onToggle, loading }) {
  return (
    <div
      onClick={!loading ? onToggle : undefined}
      style={{
        width: "52px", height: "28px", borderRadius: "100px",
        background: enabled ? "var(--accent-green)" : "var(--bg-secondary)",
        border: `2px solid ${enabled ? "var(--accent-green)" : "var(--border)"}`,
        cursor: loading ? "not-allowed" : "pointer",
        position: "relative", transition: "all 0.25s",
        opacity: loading ? 0.6 : 1, flexShrink: 0,
      }}
    >
      <div style={{
        width: "20px", height: "20px", borderRadius: "50%",
        background: enabled ? "#050810" : "var(--text-muted)",
        position: "absolute", top: "2px",
        left: enabled ? "26px" : "2px", transition: "left 0.25s",
      }} />
    </div>
  );
}

export default function Profile() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState("personal");

  // ── Personal state ─────────────────────────────────────────────
  const [phone, setPhone]         = useState(user?.phone || "");

  // ── Company state — loaded fresh from /auth/company ────────────
  const [companyName, setCompanyName]   = useState("");
  const [industry, setIndustry]         = useState("");
  const [rcNumber, setRcNumber]         = useState("");
  const [companyState, setCompanyState] = useState("");
  const [companyAddress, setCompanyAddress] = useState("");
  const [companyPhone, setCompanyPhone] = useState("");
  const [companyEmail, setCompanyEmail] = useState("");
  const [savingCompany, setSavingCompany] = useState(false);
  const [loadingCompany, setLoadingCompany] = useState(false);

  // ── Preferences state ──────────────────────────────────────────
  const [categories, setCategories]   = useState([]);
  const [prefStates, setPrefStates]   = useState([]);
  const [minValue, setMinValue]       = useState("");
  const [maxValue, setMaxValue]       = useState("");
  const [savingPrefs, setSavingPrefs] = useState(false);

  // ── Security state ─────────────────────────────────────────────
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);
  const [toggling2FA, setToggling2FA]   = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword]   = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showCurrent, setShowCurrent]   = useState(false);
  const [showNew, setShowNew]           = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);

  // ── Load company data from backend on mount ───────────────────
  useEffect(() => {
    if (user?.email) {
      fetch(`${API}/auth/2fa-status?email=${encodeURIComponent(user.email)}`)
        .then(r => r.json())
        .then(d => setTwoFactorEnabled(d.two_factor_enabled || false))
        .catch(() => {});
    }
    if (user?.company_id) {
      setLoadingCompany(true);
      authAPI.getCompany()
        .then(res => {
          const c = res.data;
          setCompanyName(c.name                || "");
          setRcNumber(c.registration_number    || "");
          setIndustry(c.industry               || "");
          setCompanyState(c.state              || "");
          setCompanyAddress(c.address          || "");
          setCompanyPhone(c.phone              || "");
          setCompanyEmail(c.email              || "");
          setCategories(c.preferred_categories || []);
          setPrefStates(c.preferred_states     || []);
          setMinValue(c.min_contract_value     || "");
          setMaxValue(c.max_contract_value     || "");
          // Keep localStorage cache in sync
          localStorage.setItem("company_profile", JSON.stringify(c));
        })
        .catch(() => {
          // Fall back to localStorage cache if API fails
          const cached = localStorage.getItem("company_profile");
          if (cached) {
            try {
              const c = JSON.parse(cached);
              setCompanyName(c.name                || "");
              setRcNumber(c.registration_number    || "");
              setIndustry(c.industry               || "");
              setCompanyState(c.state              || "");
              setCompanyAddress(c.address          || "");
              setCompanyPhone(c.phone              || "");
              setCompanyEmail(c.email              || "");
              setCategories(c.preferred_categories || []);
              setPrefStates(c.preferred_states     || []);
              setMinValue(c.min_contract_value     || "");
              setMaxValue(c.max_contract_value     || "");
            } catch {}
          }
        })
        .finally(() => setLoadingCompany(false));
    }
  }, [user]);

  // ── Save handlers ──────────────────────────────────────────────

  const saveCompany = async () => {
    if (!companyName.trim()) { toast.error("Company name is required"); return; }
    setSavingCompany(true);
    try {
      await authAPI.updateCompany({
        name:                companyName,
        registration_number: rcNumber   || undefined,
        industry:            industry   || undefined,
        state:               companyState || undefined,
        address:             companyAddress || undefined,
        phone:               companyPhone || undefined,
        email:               companyEmail || undefined,
      });
      // Cache locally so the form repopulates on next visit
      const cached = JSON.parse(localStorage.getItem("company_profile") || "{}");
      localStorage.setItem("company_profile", JSON.stringify({
        ...cached,
        name: companyName, registration_number: rcNumber,
        industry, state: companyState, address: companyAddress,
        phone: companyPhone, email: companyEmail,
      }));
      toast.success("Company profile saved!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save company profile");
    }
    setSavingCompany(false);
  };

  const savePreferences = async () => {
    setSavingPrefs(true);
    try {
      await authAPI.updateCompany({
        preferred_categories: categories,
        preferred_states:     prefStates,
        min_contract_value:   minValue ? Number(minValue) : undefined,
        max_contract_value:   maxValue ? Number(maxValue) : undefined,
      });
      const cached = JSON.parse(localStorage.getItem("company_profile") || "{}");
      localStorage.setItem("company_profile", JSON.stringify({
        ...cached,
        preferred_categories: categories,
        preferred_states: prefStates,
        min_contract_value: minValue,
        max_contract_value: maxValue,
      }));
      toast.success("Preferences saved! AI matching will update shortly.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save preferences");
    }
    setSavingPrefs(false);
  };

  const handleToggle2FA = async () => {
    setToggling2FA(true);
    try {
      const token = localStorage.getItem("access_token");
      const res   = await fetch(`${API}/auth/toggle-2fa`, {
        method: "POST", headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      setTwoFactorEnabled(data.two_factor_enabled);
      toast.success(data.message);
    } catch { toast.error("Failed to toggle 2FA"); }
    setToggling2FA(false);
  };

  const handleChangePassword = async () => {
    if (!currentPassword || !newPassword || !confirmPassword) { toast.error("Fill in all password fields"); return; }
    if (newPassword !== confirmPassword) { toast.error("Passwords do not match"); return; }
    if (newPassword.length < 8)          { toast.error("Password must be at least 8 characters"); return; }
    setChangingPassword(true);
    try {
      await authAPI.changePassword({ current_password: currentPassword, new_password: newPassword });
      toast.success("Password changed successfully!");
      setCurrentPassword(""); setNewPassword(""); setConfirmPassword("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to change password");
    }
    setChangingPassword(false);
  };

  const toggleArr = (setter, val) =>
    setter(prev => prev.includes(val) ? prev.filter(x => x !== val) : [...prev, val]);

  const pwChecks  = [newPassword.length >= 8, /[A-Z]/.test(newPassword), /[0-9]/.test(newPassword), /[^A-Za-z0-9]/.test(newPassword)];

  const tabs = [
    { id: "personal",    label: "Personal Info",      icon: User     },
    { id: "company",     label: "Company Profile",    icon: Building2 },
    { id: "preferences", label: "Tender Preferences", icon: Briefcase },
    { id: "security",    label: "Security",           icon: Shield   },
  ];

  return (
    <div style={{ maxWidth: "800px" }}>
      <div style={{ marginBottom: "28px" }}>
        <h1 style={{ fontSize: "26px", marginBottom: "6px" }}>Account Settings</h1>
        <p style={{ color: "var(--text-secondary)", fontSize: "14px" }}>Manage your profile, preferences and security</p>
      </div>

      {/* Tab bar */}
      <div style={{ display: "flex", gap: "4px", marginBottom: "24px", background: "var(--bg-secondary)", padding: "4px", borderRadius: "12px", width: "fit-content", flexWrap: "wrap" }}>
        {tabs.map(({ id, label, icon: Icon }) => (
          <button key={id} onClick={() => setActiveTab(id)} style={{
            display: "flex", alignItems: "center", gap: "8px",
            padding: "8px 16px", borderRadius: "10px", border: "none",
            background: activeTab === id ? "var(--bg-card)" : "transparent",
            color: activeTab === id ? "var(--accent-green)" : "var(--text-secondary)",
            cursor: "pointer", fontSize: "13px", fontWeight: "500", transition: "all 0.15s",
          }}>
            <Icon size={15} />{label}
          </button>
        ))}
      </div>

      {/* ── PERSONAL TAB ───────────────────────────────────────── */}
      {activeTab === "personal" && (
        <div className="card">
          <h3 style={{ fontSize: "16px", marginBottom: "20px" }}>Personal Information</h3>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" }}>
            <div>
              <label style={{ fontSize: "12px", color: "var(--text-secondary)", marginBottom: "6px", display: "block" }}>Full Name</label>
              <div style={{ position: "relative" }}>
                <User size={14} style={{ position: "absolute", left: "12px", top: "50%", transform: "translateY(-50%)", color: "var(--text-muted)" }} />
                <input value={user?.full_name || ""} readOnly style={{ ...inputStyle, paddingLeft: "34px", opacity: 0.7 }} />
              </div>
              <div style={{ fontSize: "11px", color: "var(--text-muted)", marginTop: "4px" }}>Contact support to change your name</div>
            </div>
            <div>
              <label style={{ fontSize: "12px", color: "var(--text-secondary)", marginBottom: "6px", display: "block" }}>Email Address</label>
              <div style={{ position: "relative" }}>
                <Mail size={14} style={{ position: "absolute", left: "12px", top: "50%", transform: "translateY(-50%)", color: "var(--text-muted)" }} />
                <input value={user?.email || ""} readOnly style={{ ...inputStyle, paddingLeft: "34px", opacity: 0.7 }} />
              </div>
            </div>
            <div>
              <label style={{ fontSize: "12px", color: "var(--text-secondary)", marginBottom: "6px", display: "block" }}>Phone Number</label>
              <div style={{ position: "relative" }}>
                <Phone size={14} style={{ position: "absolute", left: "12px", top: "50%", transform: "translateY(-50%)", color: "var(--text-muted)" }} />
                <input value={phone} onChange={e => setPhone(e.target.value)} placeholder="+234 801 234 5678" style={{ ...inputStyle, paddingLeft: "34px" }} />
              </div>
            </div>
            <div>
              <label style={{ fontSize: "12px", color: "var(--text-secondary)", marginBottom: "6px", display: "block" }}>Account Role</label>
              <input value={user?.role?.replace("UserRole.", "") || ""} readOnly style={{ ...inputStyle, opacity: 0.7, textTransform: "capitalize" }} />
            </div>
          </div>
        </div>
      )}

      {/* ── COMPANY TAB ────────────────────────────────────────── */}
      {activeTab === "company" && (
        <div className="card">
          <h3 style={{ fontSize: "16px", marginBottom: "4px" }}>Company Profile</h3>
          <p style={{ fontSize: "13px", color: "var(--text-secondary)", marginBottom: "20px" }}>This information powers your AI tender matching and compliance tracking.</p>
          {loadingCompany ? (
            <div style={{ textAlign: "center", padding: "40px", color: "var(--text-muted)" }}>
              <RefreshCw size={20} style={{ animation: "spin 0.8s linear infinite", marginBottom: "8px" }} />
              <style>{"@keyframes spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}"}</style>
              <div>Loading company data...</div>
            </div>
          ) : (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" }}>
              <div style={{ gridColumn: "1 / -1" }}>
                <label style={{ fontSize: "12px", color: "var(--text-secondary)", marginBottom: "6px", display: "block" }}>Company Name *</label>
                <div style={{ position: "relative" }}>
                  <Building2 size={14} style={{ position: "absolute", left: "12px", top: "50%", transform: "translateY(-50%)", color: "var(--text-muted)" }} />
                  <input value={companyName} onChange={e => setCompanyName(e.target.value)} placeholder="Your Company Ltd" style={{ ...inputStyle, paddingLeft: "34px" }} />
                </div>
              </div>
              <div>
                <label style={{ fontSize: "12px", color: "var(--text-secondary)", marginBottom: "6px", display: "block" }}>CAC / RC Number</label>
                <input value={rcNumber} onChange={e => setRcNumber(e.target.value)} placeholder="RC-123456" style={inputStyle} />
              </div>
              <div>
                <label style={{ fontSize: "12px", color: "var(--text-secondary)", marginBottom: "6px", display: "block" }}>Industry</label>
                <select value={industry} onChange={e => setIndustry(e.target.value)} style={inputStyle}>
                  <option value="">Select industry</option>
                  {INDUSTRIES.map(i => <option key={i} value={i}>{i}</option>)}
                </select>
              </div>
              <div>
                <label style={{ fontSize: "12px", color: "var(--text-secondary)", marginBottom: "6px", display: "block" }}>State</label>
                <select value={companyState} onChange={e => setCompanyState(e.target.value)} style={inputStyle}>
                  <option value="">Select state</option>
                  {STATES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <label style={{ fontSize: "12px", color: "var(--text-secondary)", marginBottom: "6px", display: "block" }}>Company Phone</label>
                <input value={companyPhone} onChange={e => setCompanyPhone(e.target.value)} placeholder="+234 801 000 0000" style={inputStyle} />
              </div>
              <div>
                <label style={{ fontSize: "12px", color: "var(--text-secondary)", marginBottom: "6px", display: "block" }}>Company Email</label>
                <input type="email" value={companyEmail} onChange={e => setCompanyEmail(e.target.value)} placeholder="info@company.com" style={inputStyle} />
              </div>
              <div style={{ gridColumn: "1 / -1" }}>
                <label style={{ fontSize: "12px", color: "var(--text-secondary)", marginBottom: "6px", display: "block" }}>Address</label>
                <input value={companyAddress} onChange={e => setCompanyAddress(e.target.value)} placeholder="123 Main Street, Port Harcourt" style={inputStyle} />
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── PREFERENCES TAB ────────────────────────────────────── */}
      {activeTab === "preferences" && (
        <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
          <div className="card">
            <h3 style={{ fontSize: "15px", fontWeight: "700", marginBottom: "4px" }}>Preferred Categories</h3>
            <p style={{ fontSize: "13px", color: "var(--text-secondary)", marginBottom: "16px" }}>Powers your AI matching — select all that apply to your business.</p>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
              {CATEGORIES.map(cat => (
                <button key={cat} onClick={() => toggleArr(setCategories, cat)} style={{ padding: "6px 14px", borderRadius: "100px", border: `1px solid ${categories.includes(cat) ? "var(--accent-green)" : "var(--border)"}`, background: categories.includes(cat) ? "var(--accent-green-dim)" : "transparent", color: categories.includes(cat) ? "var(--accent-green)" : "var(--text-secondary)", fontSize: "12px", fontWeight: "600", cursor: "pointer" }}>
                  {categories.includes(cat) ? "✓ " : ""}{cat}
                </button>
              ))}
            </div>
          </div>

          <div className="card">
            <h3 style={{ fontSize: "15px", fontWeight: "700", marginBottom: "4px" }}>Preferred States</h3>
            <p style={{ fontSize: "13px", color: "var(--text-secondary)", marginBottom: "16px" }}>Filter tenders by location.</p>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
              {STATES.map(state => (
                <button key={state} onClick={() => toggleArr(setPrefStates, state)} style={{ padding: "6px 14px", borderRadius: "100px", border: `1px solid ${prefStates.includes(state) ? "var(--accent-blue)" : "var(--border)"}`, background: prefStates.includes(state) ? "rgba(79,142,247,0.1)" : "transparent", color: prefStates.includes(state) ? "var(--accent-blue)" : "var(--text-secondary)", fontSize: "12px", fontWeight: "600", cursor: "pointer" }}>
                  {prefStates.includes(state) ? "✓ " : ""}{state}
                </button>
              ))}
            </div>
          </div>

          <div className="card">
            <h3 style={{ fontSize: "15px", fontWeight: "700", marginBottom: "16px" }}>Contract Value Range</h3>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" }}>
              <div>
                <label style={{ fontSize: "12px", color: "var(--text-secondary)", marginBottom: "6px", display: "block" }}>Min Value (₦)</label>
                <input type="number" value={minValue} onChange={e => setMinValue(e.target.value)} placeholder="e.g. 5000000" style={inputStyle} />
              </div>
              <div>
                <label style={{ fontSize: "12px", color: "var(--text-secondary)", marginBottom: "6px", display: "block" }}>Max Value (₦)</label>
                <input type="number" value={maxValue} onChange={e => setMaxValue(e.target.value)} placeholder="e.g. 500000000" style={inputStyle} />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── SECURITY TAB ───────────────────────────────────────── */}
      {activeTab === "security" && (
        <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
          {/* 2FA */}
          <div className="card">
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div style={{ display: "flex", gap: "14px", alignItems: "center" }}>
                <div style={{ width: "44px", height: "44px", background: twoFactorEnabled ? "rgba(0,212,161,0.1)" : "var(--bg-secondary)", borderRadius: "10px", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, border: `1px solid ${twoFactorEnabled ? "rgba(0,212,161,0.2)" : "var(--border)"}` }}>
                  <Shield size={20} color={twoFactorEnabled ? "var(--accent-green)" : "var(--text-muted)"} />
                </div>
                <div>
                  <div style={{ fontWeight: "700", marginBottom: "4px", display: "flex", alignItems: "center", gap: "8px" }}>
                    Two-Factor Authentication
                    {twoFactorEnabled && <span style={{ fontSize: "10px", background: "rgba(0,212,161,0.1)", color: "var(--accent-green)", border: "1px solid rgba(0,212,161,0.2)", padding: "2px 8px", borderRadius: "100px", fontWeight: "700", letterSpacing: "0.5px" }}>ENABLED</span>}
                  </div>
                  <p style={{ fontSize: "13px", color: "var(--text-secondary)", lineHeight: "1.6", margin: 0 }}>
                    {twoFactorEnabled ? "Your account is protected. A 6-digit code is required at every login." : "Add an extra layer of security — a code sent to your email at every login."}
                  </p>
                </div>
              </div>
              <Toggle enabled={twoFactorEnabled} onToggle={handleToggle2FA} loading={toggling2FA} />
            </div>
            {twoFactorEnabled && (
              <div style={{ marginTop: "16px", padding: "14px", background: "rgba(0,212,161,0.05)", border: "1px solid rgba(0,212,161,0.15)", borderRadius: "10px" }}>
                <div style={{ fontSize: "12px", color: "var(--accent-green)", fontWeight: "700", marginBottom: "4px" }}>ACTIVE PROTECTION</div>
                <div style={{ fontSize: "13px", color: "var(--text-secondary)" }}>Next login requires email verification to <strong style={{ color: "var(--text-primary)" }}>{user?.email}</strong></div>
              </div>
            )}
          </div>

          {/* Change password */}
          <div className="card">
            <div style={{ display: "flex", gap: "14px", alignItems: "center", marginBottom: "20px" }}>
              <div style={{ width: "44px", height: "44px", background: "var(--bg-secondary)", borderRadius: "10px", display: "flex", alignItems: "center", justifyContent: "center", border: "1px solid var(--border)", flexShrink: 0 }}>
                <Lock size={20} color="var(--text-muted)" />
              </div>
              <div>
                <div style={{ fontWeight: "700" }}>Change Password</div>
                <div style={{ fontSize: "13px", color: "var(--text-secondary)" }}>Use 8+ characters with uppercase, numbers and symbols</div>
              </div>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
              <div>
                <label style={{ fontSize: "13px", color: "var(--text-secondary)", marginBottom: "6px", display: "block" }}>Current Password</label>
                <div style={{ position: "relative" }}>
                  <input type={showCurrent ? "text" : "password"} value={currentPassword} onChange={e => setCurrentPassword(e.target.value)} placeholder="Enter current password" style={{ paddingRight: "44px" }} />
                  <button type="button" onClick={() => setShowCurrent(!showCurrent)} style={{ position: "absolute", right: "12px", top: "50%", transform: "translateY(-50%)", background: "none", border: "none", color: "var(--text-muted)", cursor: "pointer" }}>
                    {showCurrent ? <EyeOff size={15} /> : <Eye size={15} />}
                  </button>
                </div>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "14px" }}>
                <div>
                  <label style={{ fontSize: "13px", color: "var(--text-secondary)", marginBottom: "6px", display: "block" }}>New Password</label>
                  <div style={{ position: "relative" }}>
                    <input type={showNew ? "text" : "password"} value={newPassword} onChange={e => setNewPassword(e.target.value)} placeholder="Min 8 characters" style={{ paddingRight: "44px" }} />
                    <button type="button" onClick={() => setShowNew(!showNew)} style={{ position: "absolute", right: "12px", top: "50%", transform: "translateY(-50%)", background: "none", border: "none", color: "var(--text-muted)", cursor: "pointer" }}>
                      {showNew ? <EyeOff size={15} /> : <Eye size={15} />}
                    </button>
                  </div>
                </div>
                <div>
                  <label style={{ fontSize: "13px", color: "var(--text-secondary)", marginBottom: "6px", display: "block" }}>Confirm New Password</label>
                  <input type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} placeholder="Repeat new password"
                    style={{ borderColor: confirmPassword && confirmPassword !== newPassword ? "#ff4d6d" : undefined }} />
                </div>
              </div>
              {newPassword && (
                <div>
                  <div style={{ fontSize: "12px", color: "var(--text-muted)", marginBottom: "6px" }}>Password strength</div>
                  <div style={{ display: "flex", gap: "4px" }}>
                    {pwChecks.map((pass, i) => (
                      <div key={i} style={{ flex: 1, height: "4px", borderRadius: "2px", background: pass ? (i < 2 ? "var(--accent-orange)" : "var(--accent-green)") : "var(--bg-secondary)", transition: "background 0.2s" }} />
                    ))}
                  </div>
                </div>
              )}
              <button className="btn btn-primary" onClick={handleChangePassword} disabled={changingPassword} style={{ width: "fit-content", padding: "10px 24px" }}>
                <Lock size={14} /> {changingPassword ? "Updating..." : "Update Password"}
              </button>
            </div>
          </div>

          {/* Account info */}
          <div className="card">
            <div style={{ display: "flex", gap: "14px", alignItems: "center", marginBottom: "16px" }}>
              <div style={{ width: "44px", height: "44px", background: "var(--bg-secondary)", borderRadius: "10px", display: "flex", alignItems: "center", justifyContent: "center", border: "1px solid var(--border)", flexShrink: 0 }}>
                <Mail size={20} color="var(--text-muted)" />
              </div>
              <div><div style={{ fontWeight: "700" }}>Account Information</div></div>
            </div>
            {[
              ["Email",          user?.email],
              ["Account Role",   user?.role?.replace("UserRole.", "")],
              ["Account Status", "Active"],
              ["Login Count",    user?.login_count || 0],
              ["Last Login",     user?.last_login ? new Date(user.last_login).toLocaleString("en-NG") : "N/A"],
            ].map(([label, value]) => (
              <div key={label} style={{ display: "flex", justifyContent: "space-between", padding: "10px 0", borderBottom: "1px solid var(--border)", fontSize: "13px" }}>
                <span style={{ color: "var(--text-secondary)" }}>{label}</span>
                <span style={{ fontWeight: "600", textTransform: "capitalize" }}>{value}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Save button — shown for non-security tabs */}
      {activeTab !== "security" && (
        <button
          className="btn btn-primary"
          onClick={activeTab === "company" ? saveCompany : activeTab === "preferences" ? savePreferences : undefined}
          disabled={savingCompany || savingPrefs}
          style={{ marginTop: "20px", padding: "12px 28px" }}
        >
          <Save size={16} />
          {(savingCompany || savingPrefs) ? "Saving..." : "Save Changes"}
        </button>
      )}
    </div>
  );
}
